/**
 * Lightweight haptic feedback helpers built on the Web Vibration API.
 *
 * Reality check: `navigator.vibrate()` is supported on Android (Chrome,
 * Firefox, Edge, Samsung Internet) but iOS Safari has never implemented it, so
 * on iPhones these calls simply no-op. Everything here degrades silently:
 * - no Vibration API support → do nothing
 * - user prefers reduced motion → do nothing (treat haptics as motion)
 * - desktop browsers → no vibration hardware, so the API is absent → do nothing
 *
 * Keep the buzzes short and subtle. Haptics should feel like a confirmation,
 * never a distraction.
 */

type VibratePattern = number | number[]

const canVibrate = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
  if (typeof navigator.vibrate !== 'function') return false
  // Respect the OS-level reduced-motion preference.
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false
  } catch {
    /* matchMedia unavailable — fall through and allow vibration */
  }
  return true
}

const vibrate = (pattern: VibratePattern): void => {
  if (!canVibrate()) return
  try {
    navigator.vibrate(pattern)
  } catch {
    /* never let a haptic call break the UI */
  }
}

/** A crisp tick for selecting an option, tapping a word, or toggling a control. */
export const hapticSelection = (): void => vibrate(10)

/**
 * A tap for pressing a primary button / CTA. `style` scales the strength.
 * Defaults to a light impact.
 */
export const hapticImpact = (style: 'light' | 'medium' | 'heavy' = 'light'): void => {
  const durations = { light: 12, medium: 20, heavy: 32 }
  vibrate(durations[style])
}

/** A short celebratory pattern for milestones (result reveal, sign-up, success). */
export const hapticSuccess = (): void => vibrate([14, 36, 28])

/** A double-buzz for errors / rejected input. */
export const hapticError = (): void => vibrate([36, 28, 36])

/** A single soft buzz to warn / draw attention. */
export const hapticWarning = (): void => vibrate([22, 40, 22])

const haptics = {
  selection: hapticSelection,
  impact: hapticImpact,
  success: hapticSuccess,
  error: hapticError,
  warning: hapticWarning,
}

export default haptics
